/**
 * A seção "Fundo" do menu da câmera: quadradinhos para escolher entre sem efeito, desfoque leve e forte, as imagens e, se
 * a pessoa já enviou uma, "Seu fundo"; e embaixo "Enviar uma imagem" (ou "Trocar a minha imagem") e "Remover meu fundo".
 * O menu continua aberto ao escolher (a prévia da câmera já mostra o efeito, e dá para testar outro). Só aparece onde o
 * navegador roda o efeito (no celular de pouca força ele não aparece).
 */
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Ban, Droplet, Droplets, ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBackgroundEffect } from './BackgroundEffectProvider';
import { RoomMenuItem, RoomMenuLabel, RoomMenuRadioGroup, RoomMenuSeparator } from './roomMenu';

/** O quadradinho de uma opção: o ícone (sem efeito, desfoques) ou a própria imagem. */
function Swatch({ option }) {
    if (option.mode?.mode === 'virtual-background') {
        return <span aria-hidden="true" className="block h-full w-full bg-cover bg-center" style={{ backgroundImage: `url(${option.mode.imagePath})` }} />;
    }
    const Icon = option.id === 'blur' ? Droplet : option.id === 'blur-strong' ? Droplets : Ban;
    return (
        <span aria-hidden="true" className="flex h-full w-full items-center justify-center bg-neutral-800 text-neutral-200">
            <Icon className="h-5 w-5" />
        </span>
    );
}

/**
 * Parte visual, sem hooks. `error`: o aviso de que o efeito não rodou ou de que a imagem enviada não serviu, aqui dentro
 * também porque, com o menu aberto, o resto da página fica escondido dos leitores de tela (o aviso do palco só vale depois
 * que o menu fecha). `hasCustom`: já existe um fundo enviado (ele é uma das `options`); `onUpload(doc)` abre o seletor de
 * arquivo no documento da sala; `uploading`: a imagem escolhida está sendo preparada.
 */
export function BackgroundEffectsGroupView({ options, choice, applying, error, hasCustom = false, uploading = false, onSelect, onUpload, onRemove }) {
    return (
        <>
            <RoomMenuSeparator />
            <RoomMenuLabel>Fundo</RoomMenuLabel>
            <RoomMenuRadioGroup value={choice} onValueChange={onSelect} aria-label="Fundo da câmera" className="grid grid-cols-4 gap-1 px-1 pb-1">
                {options.map((option) => (
                    <Menu.RadioItem
                        key={option.id}
                        value={option.id}
                        aria-label={option.label}
                        title={option.label}
                        // Mantém o menu aberto: dá para ver o efeito na prévia e escolher outro.
                        onSelect={(event) => event.preventDefault()}
                        className="group flex cursor-default select-none flex-col items-center gap-1 rounded-lg p-1 outline-none focus:bg-white/10 data-[state=checked]:bg-white/5"
                    >
                        <span
                            className={cn(
                                'relative block h-11 w-full overflow-hidden rounded-md ring-1 ring-white/15',
                                'group-data-[state=checked]:ring-2 group-data-[state=checked]:ring-cyan-300',
                            )}
                        >
                            <Swatch option={option} />
                            {applying && option.id === choice && (
                                <span className="absolute inset-0 flex items-center justify-center bg-black/55">
                                    <Loader2 className="h-4 w-4 animate-spin text-white" aria-hidden="true" />
                                </span>
                            )}
                        </span>
                        <span className="text-[10px] leading-none text-neutral-300">{option.caption}</span>
                    </Menu.RadioItem>
                ))}
            </RoomMenuRadioGroup>
            {/* O menu continua aberto (preventDefault): dá para ver o efeito na prévia. O seletor de arquivo abre no documento
                DESTE item, que na janela flutuante não é o da guia. */}
            <RoomMenuItem
                icon={ImagePlus}
                description={uploading ? 'Preparando a imagem...' : 'Fica só neste navegador'}
                disabled={uploading}
                onSelect={(event) => {
                    event.preventDefault();
                    onUpload?.(event.target?.ownerDocument ?? document);
                }}
            >
                {hasCustom ? 'Trocar a minha imagem' : 'Enviar uma imagem'}
            </RoomMenuItem>
            {hasCustom && (
                <RoomMenuItem icon={Trash2} onSelect={() => onRemove?.()}>
                    Remover meu fundo
                </RoomMenuItem>
            )}
            {error && <p role="alert" className="px-3 pb-2 pt-1 text-xs leading-snug text-red-300">{error}</p>}
        </>
    );
}

export function BackgroundEffectsGroup() {
    const { supported, options, choice, applying, error, hasCustom, uploading, select, uploadCustom, removeCustom } = useBackgroundEffect();
    if (!supported) return null;
    return (
        <BackgroundEffectsGroupView
            options={options}
            choice={choice}
            applying={applying}
            error={error}
            hasCustom={hasCustom}
            uploading={uploading}
            onSelect={select}
            onUpload={uploadCustom}
            onRemove={removeCustom}
        />
    );
}
